import React from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import SelectDropdown from "../../input/SelectDropdown";
import TextInput from "../../input/TextInput";
import DateInput from "../../input/DateInput";
import { ChildRelationship } from "../../enums/ChildRelationship";
import { MilitaryLeaveReason } from "../../enums/MilitaryLeaveReason";
import { EmployeeFormValues } from "../../common/EmployeeFormValues";
import { Medical } from "../../enums/Medical";
import { buildClaimTypeOptions, ClaimPlan, filterLeaveTypesFor } from "../../domain/ClaimPlan";
import Checkbox from "../../input/Checkbox";
import LoadingSpinner from "../../structure/LoadingSpinner";
import { BenefitType, findClaimTypeLabelFor, findDefaultLeaveTypesFor, findHelpUrlFor } from "../../enums/BenefitType";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import { DisabilityProduct } from "../../enums/DisabilityProduct";
import { PhysicianFormValues } from "../../common/PhysicianFormValues";
import { PregnancyDeliveryType } from "../../enums/PregnancyDeliveryType";
import Tooltip from "../../input/Tooltip";
import { DateTime } from "luxon";
import TimeInput from "../../input/TimeInput";
import RadioGroup from "../../structure/RadioGroup";
import { WellnessScreeningType } from "../../enums/WellnessScreeningType";
import { getValueByKey } from "../../enums/KeyFinder";
import { Footnote } from "../../enums/Footnote";
import { useFormikContext } from "formik";
import { PfmlEmployeeFormValues } from "../../pfml/PfmlEmployeeFormValues";
import { useQuery } from "@apollo/client";
import { FIND_SCREENINGS_QUERY } from "../../../Queries";
import { ClaimDate } from "../../domain/ClaimDate";
import { Screening } from "../../../generated/graphql";
import { Relationship } from "../../enums/Relationship";
import { ConditionType } from "../../enums/ConditionType";
import { DisabilityEmployeeFormValues } from "../../disability/DisabilityEmployeeFormValues";
import { Party } from "../../enums/Party";
import { LeaveType } from "../../enums/LeaveType";
import { useLeavePlans } from "../../../hooks/useLeavePlans";
import { AccidentEmployeeFormValues } from "../../accident/AccidentEmployeeFormValues";
import { getDefaultBenefitSchedules, Illness } from "../../enums/Illness";
import { useBenefitSchedules } from "../../../hooks/useBenefitSchedules";
import { useAuth } from "oidc-react";
import { CriticalIllnessEmployeeFormValues } from "../../illness/CriticalIllnessEmployeeFormValues";

export type ClaimTypeProps = {
    isSubmitting?: boolean,
    headerText: string,
    detailsText?: string | React.ReactElement,
    showAdditionalInformation?: boolean,
    onlyShowMedicalRelatedLeaveTypes?: boolean,
    skipLeaveTypeServiceCall?: boolean,
    children?: React.ReactNode
}

const ClaimType = (props: ClaimTypeProps) => {
    const auth = useAuth();
    const { values, setFieldValue } = useFormikContext<EmployeeFormValues | EmployerFormValues>();
    const onlyShowMedicalRelatedLeaveTypes = props.onlyShowMedicalRelatedLeaveTypes;
    const leaveType = (values as PfmlEmployeeFormValues).leaveType;
    const conditionType = (values as DisabilityEmployeeFormValues).conditionType;
    const illnesses = (values as CriticalIllnessEmployeeFormValues).illnesses;
    const benefit = values.benefit;
    const party = values.party;
    const relationship = values.relationship;
    const policyNumber = values.policyNumber;
    const memberId = values.privacyId;
    const residencyState = values.state;
    const workState = (values as any as PfmlEmployeeFormValues).workState;
    const claimDates = (values as any as PfmlEmployeeFormValues).claimDates;
    const diagnosisDate = claimDates?.[0]?.startDate;
    const dateLastWorked = (values as EmployerFormValues).dateLastWorked;
    const earliestStartDate = dateLastWorked
        ? DateTime.fromFormat(dateLastWorked, 'MM/dd/yyyy').plus({days: 1})
        : findEarliestStartDateFor(claimDates);
    const skipLeaveTypeServiceCall = props.skipLeaveTypeServiceCall;
    const isWellnessBenefit = BenefitType.WELLNESS === benefit;

    const { data: findLeavePlans, loading: findLeavePlansLoading } = useLeavePlans(policyNumber, workState, earliestStartDate?.toISODate(), auth)
    const leavePlans = BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit ? findLeavePlans?.findLeavePlans : undefined

    const { data: findScreenings, loading: findScreeningsLoading } = useQuery(FIND_SCREENINGS_QUERY, {
        skip: !residencyState || policyNumber?.length !== 7 || !isWellnessBenefit || skipLeaveTypeServiceCall,
        variables: {
            residencyState,
            policyId: policyNumber
        }
    })
    const screeningPlans = findScreenings?.findScreenings?.map((screening: Screening) => {
        const excludedContractStates = screening.excludedContractStates;
        const screeningType = getValueByKey(WellnessScreeningType, screening.type);
        return ({
            screeningType,
            displayedClaimType: excludedContractStates.length ? `${screeningType} - excluded in ${excludedContractStates.join(',')}` : undefined,
            relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT],
            medical: [Medical.MEDICAL]
        });
    });

    const { data: findBenefitSchedules, loading: findBenefitSchedulesLoading } = useBenefitSchedules(policyNumber, memberId, diagnosisDate, relationship, auth)
    const benefitSchedules = findBenefitSchedules?.findBenefitSchedules ?? getDefaultBenefitSchedules()

    const serviceClaimPlans = leavePlans || screeningPlans
    const defaultClaimPlans = findDefaultLeaveTypesFor(benefit);
    const claimPlans: ClaimPlan[] = serviceClaimPlans?.length
        ? serviceClaimPlans
        : defaultClaimPlans

    const loading = findScreeningsLoading || findLeavePlansLoading || findBenefitSchedulesLoading
    const claimPlan = claimPlans.filter(plan =>
        (plan.leaveType ? plan.leaveType === leaveType : false)
        || (plan.conditionType ? plan.conditionType === conditionType : false)
    )?.[0];
    const claimPlanMedical = claimPlan?.medical;
    const onlyOneMedicalOption = claimPlanMedical?.length === 1;
    const onlyMedical = onlyOneMedicalOption && Medical.MEDICAL === claimPlanMedical[0];
    const onlyNonMedical = onlyOneMedicalOption && Medical.NON_MEDICAL === claimPlanMedical[0];
    React.useEffect(() => {
        defaultClaimTypeFields(onlyMedical, onlyNonMedical, setFieldValue, onlyShowMedicalRelatedLeaveTypes)
    }, [onlyMedical, onlyNonMedical, setFieldValue, onlyShowMedicalRelatedLeaveTypes]);

    const filteredLeavePlans = claimPlans.filter(leavePlan => leavePlan.relationship.includes(relationship)
        && (props.onlyShowMedicalRelatedLeaveTypes ? leavePlan.medical.includes(Medical.MEDICAL) : true))
    const claimTypeOptions = buildClaimTypeOptions(filteredLeavePlans?.length ? filteredLeavePlans : filterLeaveTypesFor(defaultClaimPlans, relationship) ?? []);
    const hasStateSpecificClaimTypes = claimTypeOptions.some(o => o.text.includes("¹"));

    const detailsText = isWellnessBenefit
        ? <>
            <div>Please specify the product(s) you are claiming wellness/health screening benefits under. (Hospital indemnity, critical illness, or accident)</div>
            <br/><br/>
            <div>Select one test or procedure performed.</div>
            <br/><br/>
            <div>We ask this because certain types of claims require different information/documentation.</div>
        </>
    : props.detailsText;

    const claimTypeLabel = findClaimTypeLabelFor(benefit);

    const isAccidentBenefit = BenefitType.ACCIDENT === benefit;
    return (
        loading
            ? <LoadingSpinner spinnerText="Loading"/>
            : <QuestionPanel headerText={props.headerText}
                             detailsText={detailsText}
                             footnotes={hasStateSpecificClaimTypes ? [Footnote.ONLY_AVAILABLE_IN_SELECT_STATES] : []}
                             panelContent={
                                 <div>
                                     {buildProductOptions(benefit)}
                                     <SelectDropdown id={claimTypeLabel.toLowerCase().replace(' ', '-')} label={claimTypeLabel}
                                                     required={true}
                                                     changeCallback={(target: HTMLSelectElement) => {
                                                         resetClaimTypeFields(setFieldValue);
                                                         if (target.value === LeaveType.MATERNITY_LEAVE) {
                                                             setFieldValue('childRelationship', ChildRelationship.BIOLOGICAL_CHILD)
                                                         }
                                                         defaultClaimTypeFields(onlyMedical, onlyNonMedical,
                                                             setFieldValue, onlyShowMedicalRelatedLeaveTypes);
                                                     }}
                                                     options={claimTypeOptions}/>
                                     {claimPlan?.medical?.length > 1 && !onlyShowMedicalRelatedLeaveTypes && <Checkbox id="medical-related"
                                                                                                                       label="Medical related"
                                                                                                                       changeCallback={() => {
                                                                                                                           removeDocumentTypeField(setFieldValue);
                                                                                                                       }}/>}
                                     {leaveType === LeaveType.BONDING_WITH_A_CHILD &&
                                         <SelectDropdown id="child-relationship" label="Relationship to child" required={true}
                                                         options={Object.keys(ChildRelationship).map(relationship => ({
                                                             id: relationship.replace(/_/g, '-').toLowerCase(),
                                                             text: (ChildRelationship as any)[relationship]
                                                         }))}
                                                         changeCallback={() => {
                                                             removeDocumentTypeField(setFieldValue);
                                                         }}/>}
                                     {leaveType === LeaveType.ACTIVE_DUTY_OR_MILITARY_EVENTS && <>
                                         <SelectDropdown id="military-leave-reason" label="Leave reason" required={true}
                                                         options={Object.keys(MilitaryLeaveReason).map(relationship => ({
                                                             id: relationship.replace(/_/g, '-').toLowerCase(),
                                                             text: (MilitaryLeaveReason as any)[relationship]
                                                         }))}
                                                         changeCallback={() => {
                                                             removeDocumentTypeField(setFieldValue);
                                                         }}
                                         />
                                         <TextInput id="military-relationship" label="Relationship to service member" required={true} maxLength={1024}/>
                                     </>}
                                     {conditionType === ConditionType.INJURY && <>
                                         {buildConditionDetailsQuestion()}
                                         {buildAccidentSpecificQuestions(isAccidentBenefit)}
                                         <TextInput id="location-of-injury" label="Where did the injury happen?" required={false} maxLength={1024}/>
                                         <TimeInput id="time-of-injury" label="What time of day did your injury happen?" required={false}/>
                                         <Checkbox id="motor-vehicle-accident" label="Was a motor vehicle involved in the accident?"/>
                                         <RadioGroup id="police-report"
                                                     label="Was a police report filed? If yes, please attach a copy."
                                                     required={isAccidentBenefit}
                                                     radioProps={[{ id: "yes", text: "Yes" }, { id: "no", text: "No" }]}/>
                                     </>}
                                     {conditionType === ConditionType.PREGNANCY && <>
                                         <DateInput id="delivery-date" label="Date of delivery" required={true}
                                                    labelContent={<Tooltip text="If you have not already delivered, enter the expected delivery date."/>}
                                                    changeCallback={(target: HTMLInputElement) => {
                                                        const deliveryDate = DateTime.fromFormat(target.value, 'MM/dd/yyyy')
                                                        setFieldValue('expectedDelivery', DateTime.now() < deliveryDate);
                                                    }}/>
                                         <SelectDropdown id="delivery-type" label="Type of delivery" required={true}
                                                         labelContent={<Tooltip text="If you have not already delivered, enter the expected delivery type."/>}
                                                         options={Object.keys(PregnancyDeliveryType).map(type => ({
                                                             id: type.replace(/_/g, '-').toLowerCase(),
                                                             text: (PregnancyDeliveryType as any)[type]
                                                         }))}/>
                                     </>}
                                     {conditionType === ConditionType.SICKNESS && <>
                                         {buildConditionDetailsQuestion()}
                                         <SelectDropdown
                                            id="illnesses"
                                            label={"What Condition/Diagnosis are you filing this claim for?"}
                                            labelContent={<span className="pds-u-typography-color-error"> *</span>}
                                            required={true}
                                            multiple={true}
                                            options={benefitSchedules.map(benefitSchedule => ({
                                                id: benefitSchedule.type.replace(/ /g, '-').toLowerCase(),
                                                text: benefitSchedule.type
                                            }))}/>
                                         {illnesses?.some((i: Illness) => i.startsWith("Infertility")) &&
                                             <RadioGroup id="medical-assistance-with-pregnancy"
                                                    label="Have you experienced infertility and undergone procedures or surgical interventions to assist with pregnancy?"
                                                    required={true}
                                                    radioProps={[{id: "yes", text: "Yes"}, {id: "no", text: "No"}]}
                                             />}
                                         <DateInput id="diagnosis-date" label="Date of diagnosis" required={false}/>
                                         <Checkbox id="has-been-hospitalized" label={'Have you been hospitalized for this condition?'}/>
                                     </>}
                                     {[ConditionType.INJURY, ConditionType.SICKNESS].includes(conditionType) && <>
                                         <DateInput id="first-visit" label={isAccidentBenefit ? 'Date first treated' : 'Date of first visit'} required={isAccidentBenefit}/>
                                         {party === Party.EMPLOYEE && <Checkbox id="work-related"
                                                    label={`Is the ${[BenefitType.HOSPITAL_INDEMNITY, BenefitType.ACCIDENT].includes(benefit) ? "injury" : "injury/illness"} related to employment?`}/>}
                                     </>}
                                     {props.showAdditionalInformation && buildInformationLink(values)}
                                     {props.children}
                                 </div>}
        >
        </QuestionPanel>
    )

    function defaultClaimTypeFields(leavePlanOnlyMedical: boolean, leavePlanOnlyNonMedical: boolean,
                                    setFieldValue: Function, onlyShowMedicalRelatedLeaveTypes: undefined | boolean) {
        if (leavePlanOnlyMedical || onlyShowMedicalRelatedLeaveTypes) {
            setFieldValue('medicalRelated', true)
        } else if(leavePlanOnlyNonMedical) {
            setFieldValue('medicalRelated', false)
        }
    }

    function buildProductOptions(benefit: BenefitType) {
        const isDisabilityBenefit = BenefitType.DISABILITY === benefit;
        if(!isDisabilityBenefit) {
            return null;
        }
        return <SelectDropdown id="product" label="Product" required={true}
                               options={Object.keys(DisabilityProduct).map(product => ({
                                   id: product.replace(/_/g, '-').toLowerCase(),
                                   text: (DisabilityProduct as any)[product]
                               }))}/>;
    }

    function buildInformationLink(values: EmployeeFormValues | EmployerFormValues | PhysicianFormValues) {
        const helpUrl = findHelpUrlFor(values.benefit)
        return helpUrl ? <div>
            <a id="leaveDescription" target="_blank" rel="noopener noreferrer"
               href={helpUrl}>More information</a>
        </div> : null;
    }

    function buildConditionDetailsQuestion() {
        return <TextInput id="condition-details" label="What was the condition being treated?" required={true} maxLength={1024}/>;
    }

    function buildAccidentSpecificQuestions(isAccidentBenefit: boolean) {
        return isAccidentBenefit ? <>
            <TextInput id="injury-details" label="Describe what happened or how the injury occurred."
                       required={true} maxLength={1024}/>
            <Checkbox id="youth-sporting-event"
                      label="Did this injury happen while taking part in an organized youth sporting event or practice?"/>
            <RadioGroup id="loss-of-life"
                        label="Did the accident result in the loss of life for the individual this accident claim is being submitted for?"
                        required={false}
                        radioProps={[{ id: "yes", text: "Yes" }, { id: "no", text: "No" }]}/>
            {(values as AccidentEmployeeFormValues).lossOfLife === 'Yes' &&
                <DateInput id="date-of-death" label="Date of death" required={false}/>}
        </> : <></>;
    }

    function resetClaimTypeFields(setFieldValue: Function) {
        setFieldValue('expectedDelivery', false);
        removeDocumentTypeField(setFieldValue);
    }

    function removeDocumentTypeField(setFieldValue: Function) {
        setFieldValue('documentType', '');
    }

    function findEarliestStartDateFor(claimDates: ClaimDate[]) {
        if(!claimDates) {
            return;
        }
        const earliestStartDate = claimDates.reduce((a, b) => {
            const aStartDate = DateTime.fromFormat(a.startDate, 'MM/dd/yyyy');
            const bStartDate = DateTime.fromFormat(b.startDate, 'MM/dd/yyyy');
            return aStartDate < bStartDate ? a : b
        }).startDate;
        return DateTime.fromFormat(earliestStartDate, 'MM/dd/yyyy');
    }
};

export default ClaimType;